<template>
    <div class="user-list-page" v-if="userHasUsers">
        <main-navigation></main-navigation>

        <div class="dashboard-header">
            <div class="dashboard-header__new" v-click-outside="() => {this.openNewUserDropdown = false}">
                <button
                    @click="newUser"
                    class="btn btn-primary"
                    type="button">
                    <md-icon>add_circle</md-icon> {{ $t('user.page.list.button.create') }}
                </button>
                <user-role-dropdown
                    :open="openNewUserDropdown"
                    :userRoles="userRoles"
                    @selectedType="selectedUserRole"
                />
            </div>

            <div class="dashboard-header__filters">
                <div class="user-list-page__header-search-flex">
                    <div class="search-container">
                        <search :input="userListActiveFilters.search" :searchData="searchData" @searchPhrase="onSearchPhraseChanged"></search>
                    </div>

                    <custom-filter
                        v-if="userListDealers.length > 0"
                        :options="userListDealers"
                        :value="userListActiveFilters.dealer"
                        :label="''"
                        :dropdown-size="340"
                        :component-size="340"
                        @selected="filterDealer"
                    ></custom-filter>

                    <custom-filter
                        v-if="userListStatuses.length > 0"
                        :options="userListStatuses"
                        :value="userListActiveFilters.status"
                        :label="''"
                        @selected="filterStatus"
                    ></custom-filter>

                    <custom-filter
                        v-if="dealerListAdvisories.length > 0 &&
                            (userListActiveFilters.filter == 'role-dealer_offer' ||
                            userListActiveFilters.filter == 'role-dealer_order_offer' ||
                            userListActiveFilters.filter == 'role-dealer_accessory')"
                        :options="dealerListAdvisories"
                        :value="userListActiveFilters.advisory"
                        :label="''"
                        @selected="filterAdvisory"
                    ></custom-filter>
                </div>

                <div v-if="userCanExportUsers">
                    <button @click="exportSelectedUsers()" type="button">
                        <md-icon>download</md-icon>
                    </button>
                    <a href="" download ref="pdfButton" class="pdf-download"></a>
                </div>

                <div class="user-list-page__header-controls">
                    <button type="button" @click="refresh()">
                        <md-icon>refresh</md-icon>
                    </button>
                </div>
            </div>
        </div>

        <div class="filter-column">
            <sidebar-filter
                v-if="userListFilters"
                :elements="userListFilters"
                :selected="userListActiveFilters.filter"
                @changed="sidebarChanged"
            ></sidebar-filter>

            <basic-links position="bottom"></basic-links>
        </div>
        <div class="list-column">
            <custom-table
                ref="table"
                :columns="tableColumns"
                :pages="userListPages"
                :meta="userListMeta"
                @load-page="loadPage"
                @remove-page="removePage"
                @on-row-click="onRowClick"
                @on-row-mouseleave="onRowMouseleave">
                <template slot="table-row" slot-scope="props">
                    <div class="col-name" v-if="props.column.field === 'name'">
                        <span class="large-line">{{ props.row.name }}</span>
                        <span class="small-line" v-if="props.row.dealer">{{ props.row.dealer }}</span>
                    </div>

                    <div class="col-options" v-else-if="props.column.field === 'options'">
                        <template v-if="props.row.actions.length > 0">
                            <button @click="props.row.showDropdown = !props.row.showDropdown" class="element-options">
                                <md-icon class="element-options">more_vert</md-icon>
                            </button>

                            <dropdown
                                @clicked="onDropdownClick($event, props.page)"
                                :open="props.row.showDropdown"
                                :options="props.row.actions"
                                :elementId="props.row.id"
                            ></dropdown>
                        </template>
                    </div>

                    <div v-else>{{ props.formattedRow[props.column.field] }}</div>
                </template>
                <div slot="empty_state">{{ $t('user.page.list.text.empty_state') }}</div>
            </custom-table>
        </div>

        <modal-user-add
            :open="openUserModal"
            :userId="selectedUser"
            :userRole="selectedRole"
            :allUserRoles="allUserRoles"
            @close="userEditEnd">
        </modal-user-add>

        <modal-confirmation
            :open="openConfirmationModal"
            :data="confirmationModalData"
            @accepted="() => {this.resendInvite(); this.openConfirmationModal = false}"
            @close="openConfirmationModal = false"
        ></modal-confirmation>

        <modal-user-delete-confirmation
            :open="openConfirmationDeleteModal"
            :data="confirmationDeleteModalData"
            :userId="this.confirmationUserId"
            @close="openConfirmationDeleteModal = false"
        ></modal-user-delete-confirmation>

        <modal-confirmation
            :open="openConfirmationForceDeleteModal"
            :data="confirmationForceDeleteModalData"
            @accepted="userForceDelete"
            @close="openConfirmationForceDeleteModal = false"
        ></modal-confirmation>

        <modal-confirmation
            :open="openConfirmationRestoreModal"
            :data="confirmationRestoreModalData"
            @accepted="userRestore"
            @close="openConfirmationRestoreModal = false"
        ></modal-confirmation>

        <notification></notification>

        <div class="is-loading" v-show="loading"></div>
    </div>
</template>

<script>
import Vue from 'vue'
import vClickOutside from 'v-click-outside'
import { mapGetters } from 'vuex'
import { MdIcon } from 'vue-material/dist/components'

import MainNavigation from '../components/navigations/MainNavigation.vue'
import BasicLinks from '../components/BasicLinks.vue'
import Dropdown from '../components/dropdowns/Dropdown.vue'
import Search from '../components/Search.vue'
import ModalUserAdd from '../components/modals/ModalUserAdd.vue'
import Notification from '../components/Notification.vue'
import UserRoleDropdown from '../components/dropdowns/UserRoleDropdown'
import SidebarFilter from '../components/SidebarFilter'
import CustomFilter from '../components/dropdowns/CustomFilter'
import ModalConfirmation from '../components/modals/ModalConfirmation.vue'
import ModalUserDeleteConfirmation from '../components/modals/ModalUserDeleteConfirmation.vue'

import CustomTable from '../components/CustomTable'
import axios from 'axios'
import config from '../plugins/config.js'

Vue.use(MdIcon)

export default {
    name: 'UserList',

    data() {
        return {
            loading: true,
            openUserModal: false,
            openNewUserDropdown: false,
            selectedRole: null,
            selectedUser: null,

            searchTimer: null,

            tableColumns: [
                {
                    label: this.$t('user.page.list.column.username'),
                    field: 'username',
                    width: '21%'
                },
                {
                    label: this.$t('user.page.list.column.name'),
                    field: 'name',
                    width: '21%',
                },
                {
                    label: this.$t('user.page.list.column.email'),
                    field: 'email',
                    width: '21%'
                },
                {
                    label: this.$t('user.page.list.column.role'),
                    field: 'role',
                    width: '17%'
                },
                {
                    label: this.$t('user.page.list.column.status'),
                    field: 'status',
                    width: '15%',
                },
                {
                    label: '',
                    field: 'options',
                    width: '5%'
                }
            ],

            openConfirmationModal: false,
            openConfirmationDeleteModal: false,
            openConfirmationForceDeleteModal: false,
            openConfirmationRestoreModal: false,

            confirmationUserId: null,
            confirmationResend: null,

            confirmationDeleteModalData: {
                acceptText: this.$t('user.page.list.confirmation.delete.ok'),
                cancelText: this.$t('user.page.list.confirmation.delete.cancel'),
                message: this.$t('user.page.list.confirmation.delete.question')
            },

            confirmationRestoreModalData: {
                acceptText: this.$t('user.page.list.confirmation.restore.ok'),
                cancelText: this.$t('user.page.list.confirmation.restore.cancel'),
                message: this.$t('user.page.list.confirmation.restore.question')
            },

            confirmationForceDeleteModalData: {
                acceptText: this.$t('user.page.list.confirmation.force_delete.ok'),
                cancelText: this.$t('user.page.list.confirmation.force_delete.cancel'),
                message: this.$t('user.page.list.confirmation.force_delete.question')
            }
        }
    },

    mounted() {
        this.$store.dispatch('FETCH_CURRENT_USER').then(() => {
            if (this.userHasUsers) {
                this.$store.commit('resetUsers');
                if (this.userHasDealers) {
                    this.$store.dispatch('FETCH_DEALERS', {page: 1, keepOtherPages: false}).then()
                }
                this.$store.dispatch('FETCH_USERS', {page: 1, keepOtherPages: false}).then(() => {
                    if (!this.userDealerOnly) {
                        this.$store.dispatch('FETCH_USER_ROLES')
                    }
                    this.$store.dispatch('FETCH_ALL_USER_ROLES')
                    this.loading = false
                })
            } else {
                this.$router.push('/');
            }
        }).catch((redirect) => {
            redirect && this.$router.push('/');
        })
    },

    methods: {
        exportSelectedUsers() {
            axios.post('users/export', this.userListActiveFilters, config.AXIOS_DEFAULTS)
                .then(data => {
                    const downloadButton = this.$refs.pdfButton
                    downloadButton.href = data.downloadUrl
                    downloadButton.click()
                })
        },

        sidebarChanged(el) {
            this.userListActiveFilters.advisory = null;
            this.$store.commit('setUserListActiveFilters', {
                ...this.userListActiveFilters,
                filter: el
            })
            this.refresh()
        },

        filterDealer(e) {
            this.$store.commit('setUserListActiveFilters', {
                ...this.userListActiveFilters,
                dealer: e.id
            })
            this.refresh()
        },

        filterStatus(e) {
            this.$store.commit('setUserListActiveFilters', {
                ...this.userListActiveFilters,
                status: e.id
            })
            this.refresh()
        },

        filterAdvisory(e) {
            this.$store.commit('setUserListActiveFilters', {
                ...this.dealerListActiveFilters,...this.userListActiveFilters,
                advisory: e.id
            })
            this.refresh()
        },

        filterType(e) {
            this.$store.commit('setUserListActiveFilters', {
                ...this.dealerListActiveFilters,...this.userListActiveFilters,
                type: e.id
            })
            this.refresh()
        },

        loadPage(page) {
            this.loading = true
            this.$store.dispatch('FETCH_USERS', {page, keepOtherPages: true}).then(() => {
                this.loading = false
            })
        },

        removePage(page) {
            this.$store.commit('removeUserListPage', page)
        },

        onRowClick(params) {
            if (!params.row.showDropdown && !params.event.target.className.includes('element-options')) {
                this.selectedUser = params.row.id
                this.selectedRole = null
                this.openUserModal = true
            }
        },

        onSearchPhraseChanged(phrase) {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer)
                this.searchTimer = null
            }

            if (this.userListActiveFilters.search === phrase) {
                return
            }

            this.searchTimer = setTimeout(() => {
                this.searchTimer = null
                this.$store.commit('setUserListActiveFilters', {
                    ...this.userListActiveFilters,
                    search: phrase.length > 0 ? phrase : void 0
                })
                this.refresh()
            }, 300)
        },

        onDropdownClick(val) {
            if (val.action === 'user-edit') {
                this.selectedUser = val.id
                this.selectedRole = null
                this.openUserModal = true
            } else if (val.action === 'user-resend-invite') {
                this.confirmationUserId = val.id
                this.confirmationResend = true
                this.openConfirmationModal = true
            } else if (val.action === 'user-send-invite') {
                this.confirmationUserId = val.id
                this.confirmationResend = false
                this.openConfirmationModal = true
            } else if (val.action === 'user-delete') {
                this.confirmationUserId = val.id
                this.openConfirmationDeleteModal = true
            } else if (val.action === 'user-force-delete') {
                this.confirmationUserId = val.id
                this.openConfirmationForceDeleteModal = true
            } else if (val.action === 'user-restore') {
                this.confirmationUserId = val.id
                this.openConfirmationRestoreModal = true
            }
        },

        userForceDelete() {
            let payload = {
                id: this.confirmationUserId,
                newUserId : null
            }

            this.$store.dispatch('FORCE_DELETE_USER', payload).then(() => {
                this.$store.commit('setNotification', [this.$t('user.page.list.confirmation.delete.notification.success')])
                this.confirmationUserId = null
                this.refreshPages()
            }).catch(() => {
                this.$store.commit('setNotification', [this.$t('user.page.list.confirmation.delete.notification.failed')])
            }).finally(() => {
                this.openConfirmationForceDeleteModal = false
            })
        },

        userRestore() {
            let payload = {
                id: this.confirmationUserId
            }

            this.$store.dispatch('RESTORE_USER', payload).then(() => {
                this.$store.commit('setNotification', [this.$t('user.page.list.confirmation.restore.notification.success')])
                this.confirmationUserId = null
                this.refreshPages()
            }).catch(() => {
                this.$store.commit('setNotification', [this.$t('user.page.list.confirmation.restore.notification.failed')])
            }).finally(() => {
                this.openConfirmationRestoreModal = false
            })
        },

        onRowMouseleave(el) {
            el.row.showDropdown = false
        },

        userEditEnd(refresh) {
            this.openUserModal = false
            if (refresh) {
                this.refreshPages()
            }
        },

        newUser() {
            if (this.userDealerOnly) {
                this.selectedUser = null
                this.selectedRole = null
                this.openUserModal = true
            } else {
                if(this.userRoles.length === 1) {
                    this.selectedUserRole(this.userRoles[0])
                    return
                }

                this.openNewUserDropdown = !this.openNewUserDropdown
            }
        },

        resendInvite() {
            this.$store.dispatch('RESEND_USER_INVITE', this.confirmationUserId).then(() => {
                this.confirmationUserId = null
                this.refreshPages()
            })
        },

        selectedUserRole(payload) {
            this.openNewUserDropdown = false
            this.selectedUser = null
            this.selectedRole = payload.key
            this.openUserModal = true
        },

        refresh() {
            this.loading = true
            this.$store.dispatch('FETCH_USERS', {page: 1, keepOtherPages: false}).then(() => {
                this.loading = false
                this.$refs.table?.scrollToTop()
            })
        },

        refreshPages() {
            this.loading = true
            this.$store.dispatch('FETCH_USER_PAGES').then(() => {
                this.loading = false
            })
        }
    },

    computed: {
        ...mapGetters(['userListPages', 'userListFilters', 'userListDealers', 'userListStatuses', 'dealerListAdvisories', 'dealerListTypes', 'dealerListActiveFilters', 'userListAdvisories', 'userListMeta', 'userListActiveFilters', 'userHasUsers', 'userHasDealers', 'userCanExportUsers', 'userDealerOnly', 'userRoles', 'allUserRoles']),

        confirmationModalData() {
            return {
                acceptText: this.$t('user.page.list.confirmation.invite.ok'),
                cancelText: this.$t('user.page.list.confirmation.invite.cancel'),
                message: this.confirmationResend ? this.$t('user.page.list.confirmation.invite.question.resend') : this.$t('user.page.list.confirmation.invite.question.send')
            }
        },

        searchData() {
            if (this.userDealerOnly) {
                return {
                    placeholder: this.$t('user.page.list.search.dealers.placeholder'),
                    tooltipText: this.$t('user.page.list.search.dealers.tooltip'),
                    countText: this.$tc('user.page.list.search.dealers.name', this.userListMeta.total)
                };
            } else {
                return {
                    placeholder: this.$t('user.page.list.search.users.placeholder'),
                    tooltipText: this.$t('user.page.list.search.users.tooltip'),
                    countText: this.$tc('user.page.list.search.users.name', this.userListMeta.total)
                };
            }
        }
    },

    directives: {
        clickOutside: vClickOutside.directive
    },

    components: {
        CustomFilter,
        SidebarFilter,
        UserRoleDropdown,
        MainNavigation,
        BasicLinks,
        CustomTable,
        Search,
        Dropdown,
        ModalUserAdd,
        Notification,
        ModalConfirmation,
        ModalUserDeleteConfirmation
    }
}
</script>

<style lang="scss">
@import '../assets/sass/ui/tooltip.scss';
@import '../assets/sass/ui/vuetable.scss';
@import '../assets/sass/ui/loading.scss';
</style>

<style lang="scss" scoped>
@import '../assets/sass/toolkit.scss';
@import '../assets/sass/ui/input.scss';
@import '../assets/sass/ui/button.scss';
@import '../assets/sass/partials/dashboard-header.scss';
@import '../assets/sass/layout/list-page.scss';

.user-list-page {
    .dashboard-header {
        .search-container {
            width: 100%;
            max-width: 640px;
        }

        .filter-component {
            margin-left: 15px;
        }
    }

    &__header-search-flex {
        display: flex;
        align-items: center;
        flex-basis: 95%;
    }

    &__header-controls {
        padding-right: 30px;
    }

    .list-column {
        font-weight: 500;
    }
}
</style>
